import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import '@/App.css';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Textarea } from './components/ui/textarea';
import { Label } from './components/ui/label';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './components/ui/select';
import { Toaster, toast } from 'sonner';
import { 
  Target, 
  BarChart3, 
  Globe2, 
  Zap, 
  TrendingUp, 
  Users,
  CheckCircle2,
  ArrowRight,
  Mail,
  Phone,
  Building2,
  MessageSquare,
  Menu,
  X,
  ChevronRight,
  Award,
  Shield,
  LineChart,
  Heart
} from 'lucide-react';
import OurImpact from './pages/OurImpact';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Logo component - uses the branded logo with matching backgrounds
const ScalientLogo = ({ variant = 'default', isOnDark = false }) => {
  // Use the new logo with dark background for dark sections (hero, footer)
  // Use original logo for white background (scrolled navbar)
  const darkBgLogo = "https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/kshyqe7b_Scalient%20New.png";
  const lightBgLogo = "https://customer-assets.emergentagent.com/job_a496982b-fa41-42f5-9942-207e322f4873/artifacts/78ul7ouf_Scalient%281%29.png";
  
  if (variant === 'footer') {
    return (
      <img 
        src={darkBgLogo}
        alt="Scalient - Marketing That Matters"
        className="h-16 md:h-20 w-auto rounded-lg"
      />
    );
  }
  
  return (
    <img 
      src={isOnDark ? darkBgLogo : lightBgLogo}
      alt="Scalient - Marketing That Matters"
      className={`h-14 md:h-16 w-auto transition-all duration-300 ${isOnDark ? 'rounded-lg' : ''}`}
    />
  );
};

// Navigation
const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: 'About' },
    { href: '#services', label: 'Services' },
    { href: '#results', label: 'Results' },
    { href: '#team', label: 'Team' },
    { href: '/our-impact', label: 'Our Impact', isRoute: true },
    { href: '#contact', label: 'Contact' },
  ];

  return (
    <nav 
      data-testid="main-navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between">
        <Link to="/" data-testid="logo-link">
          <ScalientLogo isOnDark={!isScrolled} />
        </Link>
        
        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            link.isRoute ? (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`nav-${link.label.toLowerCase().replace(' ', '-')}`}
                className={`text-sm font-medium transition-colors flex items-center gap-1 ${
                  isScrolled ? 'text-rose-500 hover:text-rose-600' : 'text-rose-300 hover:text-rose-200'
                }`}
              >
                <Heart size={14} fill="currentColor" />
                {link.label}
              </Link>
            ) : (
              <a
                key={link.href}
                href={link.href}
                data-testid={`nav-${link.label.toLowerCase()}`}
                className={`text-sm font-medium transition-colors ${
                  isScrolled ? 'text-gray-700 hover:text-teal-500' : 'text-white/90 hover:text-white'
                }`}
              >
                {link.label}
              </a>
            )
          ))}
          <Button 
            data-testid="nav-cta-button"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary px-5 py-2 rounded-md font-semibold text-sm"
          >
            Get Started
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button 
          data-testid="mobile-menu-button"
          className="md:hidden"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? (
            <X className={isScrolled ? 'text-gray-800' : 'text-white'} size={24} />
          ) : (
            <Menu className={isScrolled ? 'text-gray-800' : 'text-white'} size={24} />
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div data-testid="mobile-menu" className="md:hidden bg-white shadow-lg mt-2">
          <div className="px-6 py-4 flex flex-col gap-4">
            {navLinks.map((link) => (
              link.isRoute ? (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-rose-500 font-medium py-2 flex items-center gap-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <Heart size={16} fill="currentColor" />
                  {link.label}
                </Link>
              ) : (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-gray-700 font-medium py-2"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.label}
                </a>
              )
            ))}
            <Button 
              onClick={() => {
                setIsMobileMenuOpen(false);
                document.getElementById('contact').scrollIntoView({ behavior: 'smooth' });
              }}
              className="btn-primary w-full py-3 rounded-md font-semibold"
            >
              Get Started
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

// Hero Section
const HeroSection = () => (
  <section 
    id="hero" 
    data-testid="hero-section"
    className="mesh-gradient min-h-screen flex items-center pt-20"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div className="fade-in">
          <p className="text-teal-400 font-semibold text-sm tracking-wide mb-4">
            CONTENT AGGREGATOR WITH 1000+ CONTENT SITES
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6">
            Scaling Global Brands with Performance-Driven Advertising
          </h1>
          <p className="text-lg text-gray-300 mb-8 max-w-xl">
            Data-backed contextual, native, and programmatic campaigns delivering{' '}
            <span className="text-teal-400 font-semibold">9–10X ROAS</span> worldwide.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button 
              data-testid="hero-scale-brand-btn"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              className="btn-primary px-8 py-4 rounded-md font-bold text-base"
            >
              Scale My Brand
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button 
              data-testid="hero-strategy-call-btn"
              onClick={() => window.location.href = 'tel:+917837064744'}
              className="btn-secondary px-8 py-4 rounded-md font-semibold text-base"
            >
              Book Strategy Call
            </Button>
          </div>
        </div>
        
        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 fade-in-delay-2">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
            <p className="metric-highlight text-4xl lg:text-5xl mb-2">9-10X</p>
            <p className="text-gray-400 text-sm">Average ROAS</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
            <p className="metric-highlight text-4xl lg:text-5xl mb-2">100K+</p>
            <p className="text-gray-400 text-sm">Annual Conversions</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
            <p className="metric-highlight text-4xl lg:text-5xl mb-2">2</p>
            <p className="text-gray-400 text-sm">Nations Presence</p>
          </div>
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-lg p-6">
            <p className="metric-highlight text-4xl lg:text-5xl mb-2">16+</p>
            <p className="text-gray-400 text-sm">Enterprise Clients</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section id="about" data-testid="about-section" className="section-light py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="text-teal-500 font-semibold text-sm tracking-wide mb-4">ABOUT SCALIENT</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Performance Marketing That Delivers Measurable Results
          </h2>
          <p className="text-gray-600 text-lg mb-6 leading-relaxed">
            Scalient is a Canada-based content aggregator with 1000+ content sites serving brands worldwide. 
            With teams operating across <strong>2 nations</strong>, we deliver over <strong>100,000+ conversions annually</strong>, 
            focusing on quality over quantity.
          </p>
          <div className="space-y-4 mb-8">
            {[
              'Scalable performance campaigns',
              'Consistent 9–10X ROAS for enterprise brands',
              'Data-backed decision making',
              'Global multi-geo campaign execution'
            ].map((item, index) => (
              <div key={index} className="flex items-center gap-3">
                <CheckCircle2 className="text-teal-500 flex-shrink-0" size={20} />
                <span className="text-gray-700">{item}</span>
              </div>
            ))}
          </div>
          <Button 
            data-testid="about-learn-more-btn"
            onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
            variant="outline"
            className="border-gray-300 hover:border-teal-500 hover:text-teal-500"
          >
            Explore Our Services
            <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
        </div>
        <div className="relative">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=800&auto=format&fit=crop&q=80"
            alt="Scalient Team"
            className="rounded-lg shadow-xl w-full"
          />
          <div className="absolute -bottom-6 -left-6 bg-teal-500 text-gray-900 p-6 rounded-lg shadow-lg">
            <p className="font-extrabold text-3xl">15+</p>
            <p className="text-sm font-medium">Years Combined Experience</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const services = [
  {
    icon: Target,
    title: 'Contextual Targeting',
    description: 'Precision audience targeting based on content context for higher engagement and relevance.'
  },
  {
    icon: MessageSquare,
    title: 'Native Advertising',
    description: 'Seamlessly integrated ad experiences that match the look and feel of the platform.'
  },
  {
    icon: BarChart3,
    title: 'Programmatic Display',
    description: 'Automated, data-driven display advertising across premium inventory worldwide.'
  },
  {
    icon: TrendingUp,
    title: 'Performance Marketing',
    description: 'Results-focused campaigns optimized for conversions, leads, and revenue growth.'
  },
  {
    icon: Globe2,
    title: 'Global Media Buying',
    description: 'Strategic media procurement across international markets with local expertise.'
  },
  {
    icon: Zap,
    title: 'Conversion Optimization',
    description: 'Data-driven CRO strategies to maximize the value of every visitor.'
  },
  {
    icon: LineChart,
    title: 'ROAS-Focused Strategy',
    description: 'Custom paid media strategies engineered to deliver consistent 9-10X returns.'
  }
];

const ServicesSection = () => (
  <section id="services" data-testid="services-section" className="section-off-white py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-teal-500 font-semibold text-sm tracking-wide mb-4">OUR SERVICES</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
          Full-Funnel Performance Solutions
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Comprehensive advertising capabilities designed to scale your brand globally with measurable results.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service, index) => (
          <div 
            key={index}
            data-testid={`service-card-${index}`}
            className="service-card bg-white border border-gray-200 rounded-lg p-6 hover:border-teal-200"
          >
            <div className="w-12 h-12 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
              <service.icon className="text-teal-500" size={24} />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
            <p className="text-gray-600">{service.description}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Case Studies / Results Section
const caseStudies = [
  {
    metric: '9.2X',
    label: 'ROAS',
    description: 'E-commerce brand scaling via programmatic display and native ads.',
    industry: 'Retail'
  },
  {
    metric: '47%',
    label: 'Cost Reduction',
    description: 'Optimized media buying reduced CPA while increasing volume.',
    industry: 'SaaS'
  },
  {
    metric: '156%',
    label: 'Conversion Increase',
    description: 'Multi-geo campaign expansion with contextual targeting.',
    industry: 'Travel'
  }
];

const CaseStudiesSection = () => (
  <section id="results" data-testid="case-studies-section" className="section-dark py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-teal-400 font-semibold text-sm tracking-wide mb-4">PROVEN RESULTS</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
          Performance That Speaks for Itself
        </h2>
        <p className="text-gray-400 text-lg max-w-2xl mx-auto">
          Real outcomes from our data-driven approach to performance marketing.
        </p>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8 mb-16">
        {caseStudies.map((study, index) => (
          <div 
            key={index}
            data-testid={`case-study-${index}`}
            className="bg-white/5 border border-white/10 rounded-lg p-8 text-center"
          >
            <span className="inline-block bg-teal-500/20 text-teal-400 text-xs font-semibold px-3 py-1 rounded-full mb-4">
              {study.industry}
            </span>
            <p className="metric-highlight text-5xl lg:text-6xl mb-2">{study.metric}</p>
            <p className="text-white font-semibold text-lg mb-3">{study.label}</p>
            <p className="text-gray-400 text-sm">{study.description}</p>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-r from-teal-500/20 to-teal-400/10 border border-teal-500/30 rounded-lg p-8 text-center">
        <div className="grid sm:grid-cols-3 gap-8">
          <div>
            <p className="text-teal-400 font-extrabold text-4xl mb-1">100,000+</p>
            <p className="text-gray-300">Conversions Per Year</p>
          </div>
          <div>
            <p className="text-teal-400 font-extrabold text-4xl mb-1">9-10X</p>
            <p className="text-gray-300">Average Client ROAS</p>
          </div>
          <div>
            <p className="text-teal-400 font-extrabold text-4xl mb-1">50+</p>
            <p className="text-gray-300">Countries Reached</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Client Logos Section - With actual logos and styled text fallback
const clients = [
  { name: 'Norton', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/bz69mzw7_norton.png' },
  { name: 'Brevo', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/52vwf4pt_Brevo-Logo.webp' },
  { name: 'Best Buy', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/4pt7k70q_Best_Buy_Logo.svg.png' },
  { name: 'Zenni', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/xywo53fb_zenni.jpeg' },
  { name: "Macy's", logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/605ix5ne_Macy%27s_logo.svg.png' },
  { name: 'Chipotle', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/v8490wpp_chipotle.png' },
  { name: 'LATAM', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/zneiro2j_Latam-logo_.png' },
  { name: 'Hertz', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/97iq0l17_Hertz_logo.jpg' },
  { name: 'H10 Hotels', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/ld93060u_H10_Hotels_logo.svg.png' },
  { name: 'TotalAV', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/fopwadbp_totalav.png' },
  { name: 'NordVPN', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/uuk1ocj2_NordVPN_Logo_square.svg' },
  { name: 'JTV', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/9m226vqx_JTV_Logo.png' },
  { name: 'Jewlr', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/ktjruqz0_jewlr.jpg' },
  { name: 'MasterClass', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/mzhj7a6q_MasterClass_Logo.jpg' },
  { name: 'Coursera', logo: 'https://customer-assets.emergentagent.com/job_scalient-growth/artifacts/m8ztlqex_Coursera-Logo_600x600.svg.png' }
];

const ClientLogosSection = () => {
  return (
    <section data-testid="client-logos-section" className="section-light py-20 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 mb-12">
        <p className="text-center text-gray-500 font-medium text-sm tracking-wide">
          TRUSTED BY LEADING GLOBAL BRANDS
        </p>
      </div>
      
      <div className="relative">
        <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10"></div>
        <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10"></div>
        
        <div className="flex animate-marquee">
          {[...clients, ...clients].map((client, index) => (
            <div 
              key={index}
              className="flex-shrink-0 px-3 py-4"
            >
              <div className="bg-white border border-gray-200 rounded-xl px-6 py-4 min-w-[160px] h-20 flex items-center justify-center hover:shadow-lg transition-all duration-300">
                {client.logo ? (
                  <img 
                    src={client.logo} 
                    alt={client.name}
                    className="h-12 w-auto max-w-[130px] object-contain"
                  />
                ) : (
                  <span 
                    className="font-bold text-base tracking-tight text-gray-500 hover:text-gray-700 transition-colors duration-300"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {client.name}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const whyChooseUs = [
  {
    icon: Award,
    title: 'Performance-First Approach',
    description: 'Every campaign is engineered for measurable ROI. We don\'t chase vanity metrics—we deliver business results.'
  },
  {
    icon: Globe2,
    title: 'Global Reach, Local Expertise',
    description: 'Teams across 3 nations provide local market intelligence while executing unified global strategies.'
  },
  {
    icon: Shield,
    title: 'Data-Driven Decisions',
    description: 'Advanced analytics and attribution modeling ensure every dollar works harder for your brand.'
  },
  {
    icon: Users,
    title: 'Dedicated Partnership',
    description: 'Direct access to senior strategists who understand your business goals and growth ambitions.'
  }
];

const WhyChooseUsSection = () => (
  <section data-testid="why-choose-section" className="section-off-white py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        <div>
          <p className="text-teal-500 font-semibold text-sm tracking-wide mb-4">WHY SCALIENT</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Your Growth Partner for Scalable Results
          </h2>
          <p className="text-gray-600 text-lg mb-8">
            We're not just another agency. We're performance specialists obsessed with driving measurable growth 
            for ambitious brands ready to scale globally.
          </p>
          <Button 
            data-testid="why-choose-cta-btn"
            onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary px-6 py-3 rounded-md font-semibold"
          >
            Start Your Growth Journey
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        
        <div className="grid sm:grid-cols-2 gap-6">
          {whyChooseUs.map((item, index) => (
            <div 
              key={index}
              data-testid={`why-choose-card-${index}`}
              className="bg-white border border-gray-200 rounded-lg p-6"
            >
              <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center mb-4">
                <item.icon className="text-teal-500" size={20} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
              <p className="text-gray-600 text-sm">{item.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

// Global Team Section
const GlobalTeamSection = () => (
  <section id="team" data-testid="global-team-section" className="section-light py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-teal-500 font-semibold text-sm tracking-wide mb-4">GLOBAL PRESENCE</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
          One Team, Two Nations
        </h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Our distributed team combines global perspective with local market expertise to deliver campaigns that resonate worldwide.
        </p>
      </div>
      
      <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-12">
        {[
          { country: 'Canada', role: 'Headquarters', description: 'Strategy & Account Management', address: '1919 Elevation Road, Ottawa' },
          { country: 'India', role: 'Operations & Technology', description: 'Analytics, Development & Optimization', address: 'SCO 40 Sector 30-C, Chandigarh' }
        ].map((location, index) => (
          <div 
            key={index}
            data-testid={`team-location-${index}`}
            className="bg-white border border-gray-200 rounded-lg p-8 text-center hover:border-teal-200 transition-colors"
          >
            <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe2 className="text-teal-500" size={28} />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-1">{location.country}</h3>
            <p className="text-teal-500 font-semibold text-sm mb-2">{location.role}</p>
            <p className="text-gray-600 text-sm mb-2">{location.description}</p>
            <p className="text-gray-400 text-xs">{location.address}</p>
          </div>
        ))}
      </div>
      
      <div className="relative rounded-lg overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1200&auto=format&fit=crop&q=80"
          alt="Global Team Collaboration"
          className="w-full h-64 lg:h-80 object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/80 to-transparent flex items-center">
          <div className="px-8 lg:px-12 max-w-lg">
            <p className="text-white text-2xl lg:text-3xl font-bold mb-2">
              "Together, we scale brands beyond borders."
            </p>
            <p className="text-gray-300">— The Scalient Team</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Testimonials Section
const testimonials = [
  {
    quote: "Scalient has been instrumental in driving high-value sales for us, particularly from the US and Hong Kong markets. Their strategic approach and performance-driven campaigns consistently deliver quality customers that align perfectly with our growth goals.",
    author: "Kelly",
    role: "Marketing Director",
    company: "Mooyius.com"
  },
  {
    quote: "Partnering with Scalient has helped us achieve more consistent sales while significantly improving our ROAS. Their programmatic advertising strategy not only increased conversions but also boosted upselling opportunities across our product range.",
    author: "Brian",
    role: "CEO",
    company: "Steadfast Golf"
  },
  {
    quote: "Scalient successfully launched and scaled our affiliate program, helping us reduce overall marketing costs while increasing influencer-driven and content-based sales. Their expertise in performance marketing has made a measurable impact on our brand's growth.",
    author: "Nora",
    role: "Growth Manager",
    company: "NaturalShilajit.com"
  }
];

const TestimonialsSection = () => (
  <section data-testid="testimonials-section" className="section-off-white py-24 lg:py-32">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="text-center mb-16">
        <p className="text-teal-500 font-semibold text-sm tracking-wide mb-4">TESTIMONIALS</p>
        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
          What Our Clients Say
        </h2>
      </div>
      
      <div className="grid md:grid-cols-3 gap-8">
        {testimonials.map((testimonial, index) => (
          <div 
            key={index}
            data-testid={`testimonial-${index}`}
            className="bg-white border border-gray-200 rounded-lg p-8"
          >
            <div className="flex gap-1 mb-4">
              {[...Array(5)].map((_, i) => (
                <svg key={i} className="w-5 h-5 text-yellow-400 fill-current" viewBox="0 0 20 20">
                  <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                </svg>
              ))}
            </div>
            <p className="text-gray-700 mb-6 italic">"{testimonial.quote}"</p>
            <div>
              <p className="font-semibold text-gray-900">{testimonial.author}</p>
              <p className="text-teal-600 text-sm font-medium">{testimonial.role}</p>
              <p className="text-gray-500 text-sm">{testimonial.company}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Contact Form Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    budget: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        toast.success(response.data.message);
        setFormData({ name: '', email: '', phone: '', company: '', budget: '', message: '' });
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="section-dark py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-teal-400 font-semibold text-sm tracking-wide mb-4">GET IN TOUCH</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Scale Your Brand?
            </h2>
            <p className="text-gray-400 text-lg mb-8">
              Fill out the form and our team will reach out within 24 hours to discuss your growth goals.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="text-teal-400" size={20} />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Email us at</p>
                  <p className="text-white font-medium">hello@scalient.ca</p>
                  <p className="text-white font-medium">contact@scalient.in</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center">
                  <Globe2 className="text-teal-400" size={20} />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Based in</p>
                  <p className="text-white font-medium">Canada • India</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-teal-400" size={20} />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">Canada Office</p>
                  <p className="text-white font-medium">1919 Elevation Road</p>
                  <p className="text-gray-400 text-sm">Ottawa, Canada</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Building2 className="text-teal-400" size={20} />
                </div>
                <div>
                  <p className="text-gray-400 text-sm">India Office</p>
                  <p className="text-white font-medium">SCO 40 Sector 30-C</p>
                  <p className="text-gray-400 text-sm">Chandigarh, 160030</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-8">
            <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-gray-700 font-medium">Full Name *</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="mt-1.5"
                    placeholder="John Smith"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                  <Input
                    id="email"
                    data-testid="contact-email-input"
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    className="mt-1.5"
                    placeholder="john@company.com"
                  />
                </div>
              </div>
              
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="mt-1.5"
                    placeholder="+1 (555) 000-0000"
                  />
                </div>
                <div>
                  <Label htmlFor="company" className="text-gray-700 font-medium">Company Name *</Label>
                  <Input
                    id="company"
                    data-testid="contact-company-input"
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({...formData, company: e.target.value})}
                    className="mt-1.5"
                    placeholder="Acme Inc."
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="budget" className="text-gray-700 font-medium">Monthly Ad Budget *</Label>
                <Select
                  value={formData.budget}
                  onValueChange={(value) => setFormData({...formData, budget: value})}
                >
                  <SelectTrigger data-testid="contact-budget-select" className="mt-1.5">
                    <SelectValue placeholder="Select your budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="$5,000 - $10,000">$5,000 - $10,000</SelectItem>
                    <SelectItem value="$10,000 - $25,000">$10,000 - $25,000</SelectItem>
                    <SelectItem value="$25,000 - $50,000">$25,000 - $50,000</SelectItem>
                    <SelectItem value="$50,000 - $100,000">$50,000 - $100,000</SelectItem>
                    <SelectItem value="$100,000+">$100,000+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="message" className="text-gray-700 font-medium">Your Message *</Label>
                <Textarea
                  id="message"
                  data-testid="contact-message-input"
                  required
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  className="mt-1.5 min-h-[120px]"
                  placeholder="Tell us about your goals and current challenges..."
                />
              </div>
              
              <Button
                type="submit"
                data-testid="contact-submit-btn"
                disabled={isSubmitting}
                className="w-full btn-primary py-4 rounded-md font-bold text-base"
              >
                {isSubmitting ? 'Sending...' : 'Scale My Brand'}
                {!isSubmitting && <ArrowRight className="ml-2 h-5 w-5" />}
              </Button>
              
              <p className="text-center text-gray-500 text-xs">
                By submitting, you agree to our privacy policy. We'll never share your data.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Newsletter Section
const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await axios.post(`${API}/newsletter`, { email });
      if (response.data.success) {
        toast.success(response.data.message);
        setEmail('');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section data-testid="newsletter-section" className="section-light py-16 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="bg-gradient-to-r from-teal-500 to-teal-400 rounded-lg p-8 lg:p-12">
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">
                Get Performance Insights
              </h3>
              <p className="text-gray-800">
                Subscribe for case studies, industry trends, and optimization strategies delivered monthly.
              </p>
            </div>
            <form onSubmit={handleSubmit} data-testid="newsletter-form" className="flex flex-col sm:flex-row gap-3">
              <Input
                type="email"
                data-testid="newsletter-email-input"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 bg-white border-0"
              />
              <Button
                type="submit"
                data-testid="newsletter-submit-btn"
                disabled={isSubmitting}
                className="bg-gray-900 hover:bg-gray-800 text-white px-6 py-3 rounded-md font-semibold whitespace-nowrap"
              >
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => (
  <footer data-testid="footer" className="bg-gray-900 text-gray-400 py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid md:grid-cols-4 gap-12 mb-12">
        <div className="md:col-span-2">
          <ScalientLogo />
          <p className="mt-4 text-gray-500 max-w-md">
            A Canada and India based content aggregator with 1000+ content sites delivering measurable, scalable growth for global brands through data-driven advertising.
          </p>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Quick Links</h4>
          <ul className="space-y-2">
            {['About', 'Services', 'Results', 'Team', 'Contact'].map((link) => (
              <li key={link}>
                <a href={`#${link.toLowerCase()}`} className="hover:text-teal-400 transition-colors">
                  {link}
                </a>
              </li>
            ))}
            <li>
              <Link to="/our-impact" className="hover:text-rose-400 transition-colors flex items-center gap-1">
                <Heart size={14} fill="currentColor" className="text-rose-400" />
                Our Impact
              </Link>
            </li>
          </ul>
        </div>
        <div>
          <h4 className="text-white font-semibold mb-4">Services</h4>
          <ul className="space-y-2 text-sm">
            <li>Contextual Targeting</li>
            <li>Native Advertising</li>
            <li>Programmatic Display</li>
            <li>Performance Marketing</li>
            <li>Global Media Buying</li>
          </ul>
        </div>
      </div>
      
      <div className="border-t border-gray-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="text-sm">
          © {new Date().getFullYear()} Scalient. All rights reserved.
        </p>
        <div className="flex gap-6 text-sm">
          <a href="#" className="hover:text-teal-400 transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-teal-400 transition-colors">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// Home Page Component
const HomePage = () => (
  <>
    <Navigation />
    <main>
      <HeroSection />
      <AboutSection />
      <ServicesSection />
      <CaseStudiesSection />
      <ClientLogosSection />
      <WhyChooseUsSection />
      <GlobalTeamSection />
      <TestimonialsSection />
      <ContactSection />
      <NewsletterSection />
    </main>
    <Footer />
  </>
);

// Main App Component with Routing
function App() {
  return (
    <Router>
      <div className="App">
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/our-impact" element={<OurImpact />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
